import React from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faShieldAlt, faStar } from "@fortawesome/free-solid-svg-icons";
import "./PayPageStyle.css";

interface State {
  show: boolean;
  success: boolean;
  errorMessage: string;
  orderID: string | false;
  clientId: string;
}

interface Actions {
  handleCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  createOrder: (data: any, actions: any) => Promise<string>;
  onApprove: (data: any, actions: any) => Promise<void>;
  onError: (err: any) => void;
}

interface Refs {
  endOfSide: React.RefObject<HTMLDivElement>;
}

interface Props {
  state: State;
  actions: Actions;
  refs: Refs;
}

const PayPageView: React.FC<Props> = ({ state, actions, refs }) => {
  return (
    <div className="ja-pay-pageWrapper">
      <div className="ja-pay-mainContainer">
        
        {/* Header */}
        <div className="ja-pay-header">
          <h2>Junior Alarm Monatsabo</h2>
          <p className="ja-pay-subtitle">
            Die professionelle Alarmierungs-Komplettlösung für Jugendfeuerwehren
          </p>
        </div>

        <PayPalScriptProvider options={{ clientId: state.clientId, currency: "EUR" }}>
          
          {/* Bildervorschau */}
          <div className="ja-pay-mediaCard">
            <div className="ja-pay-productImgGrid">
              <div className="ja-pay-imgWrapper">
                <img
                  src="https://ff-schwebda.de/JuniorAlarm/V2/Images/App.jpg"
                  alt="JuniorAlarm App"
                />
              </div>
              <div className="ja-pay-imgWrapper">
                <img
                  src="https://ff-schwebda.de/JuniorAlarm/V2/Images/Web.png"
                  alt="JuniorAlarm Web"
                />
              </div>
            </div>
          </div>

          {/* Hauptinhalt Grid */}
          <div className="ja-pay-contentGrid">
            
            {/* Linke Spalte: Features & Vorteile */}
            <div className="ja-pay-infoCard">
              <div className="ja-pay-cardHeader">
                <FontAwesomeIcon icon={faShieldAlt} className="ja-pay-cardIcon" />
                <h3>Das smarte Alarmsystem für die Jugend</h3>
              </div>
              <p className="ja-pay-introText">
                Mit dem <strong>JuniorAlarm Monatsabo</strong> bist du immer bereit für den Ernstfall.
                JuniorAlarm ist das erste Alarmsystem speziell entwickelt für die Jugend im Ehrenamt und
                bietet ein umfassendes Toolset, um junge Leute sicher, schnell und effizient zu alarmieren.
              </p>

              <div className="ja-pay-section">
                <h4><FontAwesomeIcon icon={faStar} /> Warum JuniorAlarm?</h4>
                <ul className="ja-pay-featureList">
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Schnelle & einfache Alarmierung:</strong> Mit einem Klick alle Mitglieder erreichen – direkt auf ihre Smartphones.</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Kritische Hinweise:</strong> Die Meldung erfolgt zuverlässig – auch wenn das Smartphone stummgeschaltet ist.</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Intuitive Bedienung:</strong> Einfache Handhabung, speziell abgestimmt auf Jugendleiter & Betreuer.</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Individuelle Anpassung:</strong> Erstelle personalisierte Alarme und Einsatzstichworte.</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Sichere Kommunikation:</strong> Verschlüsselte Nachrichten und direkte Rückmeldungen in Echtzeit.</span>
                  </li>
                </ul>
              </div>

              <div className="ja-pay-section">
                <h4><FontAwesomeIcon icon={faStar} /> Das bietet das Monatsabo:</h4>
                <ul className="ja-pay-featureList">
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Uneingeschränkter Zugriff:</strong> Nutze alle Funktionen von JuniorAlarm ohne Limitierungen.</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Regelmäßige Updates:</strong> Profitiere von kontinuierlichen Verbesserungen und neuen Features.</span>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faCheckCircle} className="ja-pay-checkIcon" />
                    <span><strong>Priorisierter Support:</strong> Erhalte schnelle Hilfe bei Fragen und technischen Problemen.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Rechte Spalte: Preis & Checkout */}
            <div className="ja-pay-checkoutCard">
              <div className="ja-pay-priceContainer">
                <span className="ja-pay-priceLabel">Monatsabo</span>
                <div className="ja-pay-priceTag">
                  <span className="ja-pay-amount">20,00 €</span>
                  <span className="ja-pay-period">/ Monat</span>
                </div>
                <p className="ja-pay-priceHint">Jederzeit kündbar • Inkl. aller Features</p>
              </div>

              <div className="ja-pay-checkboxGroup">
                <label className="ja-pay-checkboxItem">
                  <input
                    type="checkbox"
                    id="accept-agbs"
                    checked={state.show}
                    onChange={actions.handleCheckboxChange}
                  />
                  <span>
                    Ich akzeptiere die{" "}
                    <a
                      href="https://mbsoftwaresolutions.de/produkte/apps/junioralarm/agb"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      AGBs
                    </a>
                  </span>
                </label>
              </div>

              {state.errorMessage && (
                <div className="ja-pay-errorBox">
                  {state.errorMessage}
                </div>
              )}

              {state.show && (
                <div className="ja-pay-paypalWrapper" ref={refs.endOfSide}>
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "rect" }}
                    createOrder={actions.createOrder}
                    onApprove={actions.onApprove}
                    onError={actions.onError}
                  />
                </div>
              )}
            </div>

          </div>

        </PayPalScriptProvider>
      </div>
    </div>
  );
};

export default PayPageView;